import { Box } from "@mui/material";
import {
  RestaurantMenuOutlined as MenuIcon,
  AutoAwesomeOutlined as AiIcon,
  AddCircleOutlineOutlined as AddMealIcon,
} from "@mui/icons-material";
import { useState, type ReactNode } from "react";
import { Card, EmptyState, TextButton } from "../ui/primitives";
import { v2 } from "../ui/v2Tokens";
import MealCard from "./MealCard";
import PickSavedMealDialog from "../dialogs/PickSavedMealDialog";
import SaveMealAsTemplateDialog from "../dialogs/SaveMealAsTemplateDialog";
import { saveMealToLibrary } from "../../../../services/NutritionV2Service";
import { cloneItems, createMeal, newId, type MealV2 } from "../model/nutritionV2Model";

type Props = {
  meals: MealV2[];
  onChange: (meals: MealV2[]) => void;
  onNotify?: (message: string) => void;
  /**
   * Rendered above the add-meal actions and pinned WITH them.
   *
   * The targets bar goes here. It is passed in rather than rendered by the page as a sibling
   * because the two have to stick as ONE element: the numbers and the buttons that change them
   * are the block a trainer works from, and two separately-sticky elements would need the first
   * one's height as a constant to offset the second — a constant that changes with the viewport.
   */
  stickyHeader?: ReactNode;
  /**
   * How far down the pinned block comes to rest — the height of the page's own sticky header,
   * measured by the page. `0` (the default) means "stick to the top of the scroll container",
   * which is right when there is nothing above it and wrong when there is.
   */
  stickyTop?: number;
};

const resequence = (meals: MealV2[]): MealV2[] => meals.map((m, i) => ({ ...m, order: i }));

/**
 * Whether the head pins, by width — see the long note at the render site for why it does not on a
 * phone.
 *
 * Exported because a responsive `sx` value cannot be read back from the DOM in jsdom: MUI wraps
 * even the `xs` value in `@media (min-width:0px)` and jsdom evaluates no media queries, so
 * `toHaveStyle({ position: 'sticky' })` sees an empty string at every width. Asserting the object
 * is what keeps "it still pins on desktop" covered at all.
 */
export const STICKY_HEAD_POSITION = { xs: "static", md: "sticky" } as const;

/**
 * The pinned actions run shorter than a TextButton does anywhere else — see the block below.
 *
 * Except on a phone, where the block does not pin at all (so there is no height to save) and 30px
 * is not a target a thumb can find.
 */
const compactBtn = { height: { xs: "40px", md: "30px" }, padding: "0 11px", fontSize: "12.5px" };

const MealsList = ({ meals, onChange, onNotify, stickyHeader, stickyTop = 0 }: Props) => {
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [savingMeal, setSavingMeal] = useState<MealV2 | null>(null);

  const addMeal = () => onChange(resequence([...meals, createMeal(meals.length)]));

  const addMealFromTemplate = (result: { name: string; items: MealV2["items"] }) => {
    const base = createMeal(meals.length, { name: result.name, items: result.items });
    onChange(resequence([...meals, base]));
    setTemplatePickerOpen(false);
  };

  const updateMeal = (uid: string, next: MealV2) =>
    onChange(meals.map((m) => (m.uid === uid ? next : m)));

  const deleteMeal = (uid: string) => onChange(resequence(meals.filter((m) => m.uid !== uid)));

  const duplicateMeal = (uid: string) => {
    const index = meals.findIndex((m) => m.uid === uid);
    if (index < 0) return;
    const source = meals[index];
    const copy: MealV2 = {
      ...source,
      uid: newId("meal"),
      name: `${source.name} (עותק)`,
      items: cloneItems(source.items),
      alternatives: (source.alternatives || []).map((alt) => ({
        ...alt,
        uid: newId("alt"),
        items: cloneItems(alt.items),
      })),
    };
    const next = [...meals];
    next.splice(index + 1, 0, copy);
    onChange(resequence(next));
  };

  const moveMeal = (uid: string, direction: -1 | 1) => {
    const index = meals.findIndex((m) => m.uid === uid);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= meals.length) return;
    const next = [...meals];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(resequence(next));
  };

  const saveTemplate = (name: string) => {
    if (!savingMeal) return;
    void saveMealToLibrary(name, savingMeal);
    setSavingMeal(null);
    onNotify?.(`"${name}" נוספה לארוחות השמורות של החברה`);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {/*
        The targets and the actions that change them, pinned as one block.

        A trainer adding the eighth meal of a menu is working against five numbers that are, by
        then, several screens up. Pinning them means the answer to "how much protein is still
        missing" is on screen at the moment the food is added, rather than after a scroll back.

        `stickyTop` rather than `top: 0`: this comes to rest UNDER the page's own sticky header,
        whose height only the page can measure. The zIndex sits BELOW that header's 30, so the two
        never fight and the day-of-week tabs it carries are never covered.

        The whole block is the same size stuck as unstuck — no compact mode. A sticky element keeps
        its place in normal flow, so anything that shrinks on sticking pulls the page up under the
        reader's eyes; a jump-free version of that needs a sentinel observer and a fixed flow
        height, which is more machinery than the saved centimetre is worth.

        ── and on a phone it does not stick at all ──

        At 375px the targets grid drops to two columns and stands about 290px tall. Pinned under a
        header that wrapped to four rows, the two came to roughly 670px of a 667px screen: the
        chrome meant to help you read the menu was covering the entire menu. Below `md` this simply
        sits at the top of the meals and scrolls away, and the page header carries a one-line
        TargetsMiniStrip in its place — which answers the same question in a thirtieth of the space
        and scrolls back here when tapped.
      */}
      <Box
        data-testid="meals-sticky-head"
        sx={{
          position: STICKY_HEAD_POSITION,
          // ignored while static, so there is nothing to branch on here
          top: `${stickyTop}px`,
          zIndex: 25,
          display: "flex",
          flexDirection: "column",
          // tight: every pixel of this block is a pixel of menu permanently off screen
          gap: "8px",
          // the meals scroll THROUGH this, so it cannot be transparent. Same treatment as the
          // page header it comes to rest under, so the two read as one piece of chrome.
          background: "rgba(246,248,250,.88)",
          backdropFilter: "saturate(180%) blur(12px)",
          // the block is edge-to-edge while the page gutter is not, so a negative margin lets the
          // background cover the full width and the padding puts the content back where it was.
          // Nothing to bleed over on a phone: it is not chrome there, just the top of the list.
          marginInline: { xs: 0, md: "-8px" },
          paddingInline: { xs: 0, md: "8px" },
          paddingBottom: "8px",
        }}
      >
        {stickyHeader}

        {/* action bar — one notch shorter than a standalone TextButton, because it is pinned */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          <TextButton tone="green" onClick={addMeal} sx={compactBtn}>
            + הוסף ארוחה חדשה
          </TextButton>
          <TextButton onClick={() => setTemplatePickerOpen(true)} sx={compactBtn}>
            + הוסף מארוחה שמורה
          </TextButton>
          {/* not part of building a menu by hand — pushed to the end so it stops competing
              with the two buttons that are */}
          <TextButton tone="purple" disabled title="בקרוב" sx={{ ...compactBtn, marginInlineStart: "auto" }}>
            <AiIcon sx={{ fontSize: 15 }} />
            בנה עם AI
          </TextButton>
        </Box>
      </Box>

      {meals.length === 0 ? (
        <Card>
          <EmptyState icon={MenuIcon} text="התפריט ריק — התחל בהוספת ארוחה חדשה, או בחר מהארוחות השמורות של החברה.">
            <Box sx={{ display: "flex", gap: "8px", marginTop: "4px" }}>
              <TextButton tone="green" onClick={addMeal}>
                + הוסף ארוחה חדשה
              </TextButton>
              <TextButton onClick={() => setTemplatePickerOpen(true)}>
                + הוסף מארוחה שמורה
              </TextButton>
            </Box>
          </EmptyState>
        </Card>
      ) : (
        meals.map((meal, i) => (
          <MealCard
            key={meal.uid}
            meal={meal}
            canMoveUp={i > 0}
            canMoveDown={i < meals.length - 1}
            onChange={(next) => updateMeal(meal.uid, next)}
            onDelete={() => deleteMeal(meal.uid)}
            onDuplicate={() => duplicateMeal(meal.uid)}
            onMove={(direction) => moveMeal(meal.uid, direction)}
            onSaveAsTemplate={() => setSavingMeal(meal)}
          />
        ))
      )}

      {/* A slot where the next meal will land — the day reads top to bottom, so the place
          to continue it is at the end of the list, not only in the bar above. */}
      {meals.length > 0 && (
        <Box
          component="button"
          type="button"
          aria-label="הוסף ארוחה חדשה בסוף התפריט"
          onClick={addMeal}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            width: "100%",
            height: "54px",
            borderRadius: v2.rLg,
            border: `1.5px dashed ${v2.line}`,
            background: "transparent",
            color: v2.ink3,
            fontSize: "14px",
            fontWeight: 500,
            fontFamily: "inherit",
            cursor: "pointer",
            transition: "all .14s",
            "&:hover": { borderColor: v2.brand, color: v2.brand, background: v2.brandWash },
          }}
        >
          <AddMealIcon sx={{ fontSize: 20 }} />
          הוסף ארוחה חדשה
        </Box>
      )}

      {meals.length > 0 && (
        <Box sx={{ fontSize: "12.5px", color: v2.ink4, textAlign: "center", marginTop: "4px" }}>
          חלופות וארוחות חלופיות אינן נספרות בסיכום היומי.
        </Box>
      )}

      <PickSavedMealDialog
        open={templatePickerOpen}
        onClose={() => setTemplatePickerOpen(false)}
        title="הוסף ארוחה מארוחה שמורה"
        entityLabel="ארוחה"
        onConfirm={addMealFromTemplate}
      />

      <SaveMealAsTemplateDialog
        open={Boolean(savingMeal)}
        onClose={() => setSavingMeal(null)}
        defaultName={savingMeal?.name || ""}
        itemCount={savingMeal?.items.length || 0}
        onSave={saveTemplate}
      />
    </Box>
  );
};

export default MealsList;
